import { useEffect, useState } from "react";
import { X, Phone, Loader2, CheckCircle2, Calendar, BookOpen, User as UserIcon, MapPin, MessageCircle } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { trackEvent } from "../lib/analytics";
import { isValidPhoneNumber, countryOf } from "../lib/phone";
import { SOCIAL } from "../data/siteData";
import PhoneInputE164 from "./PhoneInputE164";

/**
 * V10 P1 — "Get Free Consultation" / "Book Free Demo Class" modal.
 *
 * A single reusable component reachable from any CTA button on the site.
 * Submissions route to POST /api/callback-request → drop into the existing
 * `inquiries` collection with subject="Callback Request" / "Free Demo Request"
 * so they surface automatically in the Unified Inbox + xlsx export.
 *
 * Props:
 *   - open (bool), onClose (fn)
 *   - variant: "consultation" | "demo"  — controls heading + cta_source field
 *   - defaultInterest (str) — pre-fills e.g. "Class 10 CBSE Maths"
 *   - defaultCity (str)
 */

const VARIANTS = {
    consultation: {
        eyebrow: "Get Free Consultation",
        title: "Talk to a senior tutor — no obligation",
        sub: "Tell us about your child. We'll call back within 24 hours, answer questions and offer a free 1:1 demo if useful.",
        cta: "Request a Free Consultation",
        cta_source: "consultation",
    },
    demo: {
        eyebrow: "Book Free 1:1 Demo Class",
        title: "Watch the tutor teach — before any payment",
        sub: "We schedule a full 1:1 demo with a verified specialist within 24-48 hours. No card. No commitment.",
        cta: "Book My Free Demo Class",
        cta_source: "demo",
    },
};

export default function CallbackRequestModal({
    open,
    onClose,
    variant = "consultation",
    defaultInterest = "",
    defaultCity = "",
    preferCountry,
}) {
    const cfg = VARIANTS[variant] || VARIANTS.consultation;
    const [name, setName] = useState("");
    const [phone, setPhone] = useState("");
    const [email, setEmail] = useState("");
    const [city, setCity] = useState(() => defaultCity || "");
    const [preferredTime, setPreferredTime] = useState("");
    const [interest, setInterest] = useState(() => defaultInterest || "");
    const [submitting, setSubmitting] = useState(false);
    const [success, setSuccess] = useState(false);
    const [error, setError] = useState("");

    // Body scroll lock while modal is open. State resets happen naturally on
    // unmount because we render `null` when !open (so useState re-initialises).
    useEffect(() => {
        if (!open) return;
        document.body.style.overflow = "hidden";
        return () => { document.body.style.overflow = ""; };
    }, [open]);

    // Close on Escape
    useEffect(() => {
        if (!open) return;
        const h = (e) => { if (e.key === "Escape") onClose?.(); };
        document.addEventListener("keydown", h);
        return () => document.removeEventListener("keydown", h);
    }, [open, onClose]);

    if (!open) return null;

    const submit = async (e) => {
        e.preventDefault();
        if (submitting) return;
        setError("");
        const nameOk = name.trim().length >= 2;
        const phoneOk = isValidPhoneNumber(phone);
        if (!nameOk && !phoneOk) { setError("Please enter your name and a valid phone number with country code."); return; }
        if (!nameOk) { setError("Please enter your full name."); return; }
        if (!phoneOk) { setError("Please enter a valid phone number with country code."); return; }

        setSubmitting(true);
        try {
            // Light attribution capture
            const attribution = {
                page_path: window.location.pathname,
                referrer: document.referrer || null,
                utm_source: new URLSearchParams(window.location.search).get("utm_source") || null,
                utm_medium: new URLSearchParams(window.location.search).get("utm_medium") || null,
                utm_campaign: new URLSearchParams(window.location.search).get("utm_campaign") || null,
            };
            await api.post("/callback-request", {
                name: name.trim(),
                phone: phone.trim(),
                phone_country: countryOf(phone) || null,
                email: email.trim() || null,
                city: city.trim() || null,
                preferred_time: preferredTime.trim() || null,
                interest: interest.trim() || null,
                cta_source: cfg.cta_source,
                attribution,
            });
            trackEvent("callback_request_submit", { variant: cfg.cta_source });
            setSuccess(true);
        } catch (err) {
            setError(formatApiError(err.response?.data?.detail) || "Submission failed — please try again or reach us via the Contact page.");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div
            data-testid="callback-modal"
            className="fixed inset-0 z-[100] flex items-end md:items-center justify-center p-0 md:p-4"
            onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
        >
            {/* Backdrop */}
            <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm" />

            {/* Modal */}
            <div className="relative bg-white rounded-t-3xl md:rounded-2xl w-full max-w-lg max-h-[92vh] overflow-y-auto shadow-2xl animate-in slide-in-from-bottom-4 duration-300">
                <button
                    type="button"
                    onClick={onClose}
                    data-testid="callback-modal-close"
                    aria-label="Close"
                    className="absolute top-3 right-3 p-2 rounded-full hover:bg-slate-100 text-slate-500"
                >
                    <X className="w-5 h-5" />
                </button>

                <div className="p-6 md:p-8">
                    <div className="text-[10px] font-bold tracking-widest text-amber-600 uppercase mb-1">{cfg.eyebrow}</div>
                    <h3 className="font-heading text-2xl md:text-3xl font-bold text-[#0F172A] mb-2">{cfg.title}</h3>
                    <p className="text-sm text-slate-600 mb-6">{cfg.sub}</p>

                    {success ? (
                        <div data-testid="callback-success" className="text-center py-6">
                            <div className="w-16 h-16 mx-auto bg-emerald-100 rounded-full flex items-center justify-center mb-4">
                                <CheckCircle2 className="w-9 h-9 text-emerald-600" />
                            </div>
                            <h4 className="font-heading text-xl font-bold text-[#0F172A] mb-2">Thank you, {name.split(" ")[0]}!</h4>
                            <p className="text-sm text-slate-600 mb-5">A team member will call you back within 24 hours. For urgent help, tap below.</p>
                            <div className="flex flex-wrap justify-center gap-2">
                                <a href="tel:+917000530316" className="inline-flex items-center gap-2 bg-[#1E3A8A] text-white px-4 py-2.5 rounded-lg text-sm font-semibold hover:bg-blue-800">
                                    <Phone className="w-4 h-4" /> Call now
                                </a>
                                <a href={`https://wa.me/${SOCIAL.whatsapp}?text=${encodeURIComponent(`Hi, I'm ${name}. I just requested a callback — `)}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 bg-emerald-500 text-white px-4 py-2.5 rounded-lg text-sm font-semibold hover:bg-emerald-600">
                                    <MessageCircle className="w-4 h-4" /> WhatsApp
                                </a>
                                <button type="button" onClick={onClose} className="inline-flex items-center gap-2 bg-slate-100 text-slate-700 px-4 py-2.5 rounded-lg text-sm font-semibold hover:bg-slate-200">
                                    Close
                                </button>
                            </div>
                        </div>
                    ) : (
                        <form onSubmit={submit} className="space-y-3" data-testid="callback-form">
                            <Field icon={UserIcon} label="Your name *">
                                <input
                                    type="text"
                                    value={name}
                                    onChange={(e) => setName(e.target.value)}
                                    placeholder="e.g. Priya Sharma"
                                    data-testid="callback-name"
                                    className="w-full px-3 py-2.5 text-sm bg-transparent focus:outline-none"
                                />
                            </Field>
                            <Field icon={Phone} label="Phone (we'll call back) *">
                                <PhoneInputE164
                                    value={phone}
                                    onChange={setPhone}
                                    required
                                    preferCountry={preferCountry}
                                    testid="callback-phone"
                                    className="px-1"
                                />
                            </Field>
                            <Field icon={MapPin} label="City (optional)">
                                <input
                                    type="text"
                                    value={city}
                                    onChange={(e) => setCity(e.target.value)}
                                    placeholder="e.g. Jabalpur, Bhopal, Mumbai"
                                    data-testid="callback-city"
                                    className="w-full px-3 py-2.5 text-sm bg-transparent focus:outline-none"
                                />
                            </Field>
                            <Field icon={BookOpen} label="What does your child need help with? (optional)">
                                <input
                                    type="text"
                                    value={interest}
                                    onChange={(e) => setInterest(e.target.value)}
                                    placeholder="e.g. Class 10 CBSE Maths, IB DP Chemistry"
                                    data-testid="callback-interest"
                                    className="w-full px-3 py-2.5 text-sm bg-transparent focus:outline-none"
                                />
                            </Field>
                            <Field icon={Calendar} label="Best time to call you (optional)">
                                <input
                                    type="text"
                                    value={preferredTime}
                                    onChange={(e) => setPreferredTime(e.target.value)}
                                    placeholder="e.g. After 6 pm IST, Sat morning"
                                    data-testid="callback-time"
                                    className="w-full px-3 py-2.5 text-sm bg-transparent focus:outline-none"
                                />
                            </Field>

                            {error && (
                                <div data-testid="callback-error" className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
                                    {error}
                                </div>
                            )}

                            <button
                                type="submit"
                                disabled={submitting}
                                data-testid="callback-submit"
                                className="w-full inline-flex items-center justify-center gap-2 bg-amber-500 text-[#0F172A] font-bold px-5 py-3 rounded-lg hover:bg-amber-400 disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
                            >
                                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                                {submitting ? "Sending…" : cfg.cta}
                            </button>

                            <p className="text-[11px] text-slate-500 text-center pt-1">
                                No advance fee · No lock-in · We&apos;ll never share your number.
                            </p>
                        </form>
                    )}
                </div>
            </div>
        </div>
    );
}

function Field({ icon: Icon, label, children }) {
    return (
        <div>
            <label className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider mb-1 block">{label}</label>
            <div className="flex items-center gap-2 border border-slate-200 rounded-lg px-3 focus-within:ring-2 focus-within:ring-[#3B82F6] focus-within:border-[#3B82F6] transition-shadow">
                <Icon className="w-4 h-4 text-slate-400 shrink-0" />
                <div className="flex-1 min-w-0">{children}</div>
            </div>
        </div>
    );
}
